/**
 * SessionKeepAliveService
 *
 * This service prevents session expiration during long planning sessions by:
 * 1. Periodically pinging the server to keep the Rails session alive
 * 2. Refreshing the Firebase auth token to ensure it stays valid
 * 3. Re-syncing Firebase auth with Rails if the session has expired
 *
 * This is important for the plan_trail app where users may spend hours
 * planning without making server requests, which could cause their session
 * to expire before they save their work.
 */

import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { forceSync, hasFirebaseUser } from '../../../utils/firebase/authSync';

// Ping interval: 10 minutes (in milliseconds)
const KEEP_ALIVE_INTERVAL = 10 * 60 * 1000;

let intervalId: ReturnType<typeof setInterval> | null = null;
let isRunning = false;
let authReady = false;

/**
 * Wait for Firebase auth to finish initializing (restoring state from persistence).
 * This prevents false-positive "expired" detections during page startup,
 * where currentUser is null simply because auth hasn't loaded yet.
 */
function waitForAuthReady(): Promise<void> {
  if (authReady) return Promise.resolve();

  return new Promise((resolve) => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, () => {
      unsubscribe();
      authReady = true;
      resolve();
    });
  });
}

/**
 * Ping the server to keep the Rails session alive
 * Returns whether the user is still authenticated on the server
 */
async function pingServer(): Promise<boolean> {
  try {
    const response = await fetch('/session/keep_alive', {
      method: 'GET',
      credentials: 'include', // Important: include cookies for session
      headers: {
        'Accept': 'application/json',
      },
    });

    if (response.ok) {
      const data = await response.json();
      return data.authenticated === true;
    }
    return false;
  } catch (error) {
    console.warn('[SessionKeepAlive] Failed to ping server:', error);
    return false;
  }
}

/**
 * Refresh the Firebase auth token
 * Firebase tokens expire after 1 hour, but the SDK auto-refreshes them
 * when getIdToken(true) is called with forceRefresh=true
 */
async function refreshFirebaseToken(): Promise<boolean> {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (user) {
      // Force refresh the token
      await user.getIdToken(true);
      return true;
    }
    return false;
  } catch (error) {
    console.warn('[SessionKeepAlive] Failed to refresh Firebase token:', error);
    return false;
  }
}

/**
 * Perform a keep-alive check
 */
async function performKeepAlive(): Promise<void> {
  // Skip entirely if there's no logged-in user (avoid reload loop for anonymous users)
  if (!(window as any).user) {
    return;
  }

  // Wait for Firebase auth to finish initializing before checking.
  // During page startup, currentUser is null until Firebase restores
  // auth state from IndexedDB. Without this wait, the check below
  // would misinterpret "not yet loaded" as "expired" and trigger
  // an infinite reload loop — especially when window.confirm() blocks
  // the event loop during session restoration prompts.
  await waitForAuthReady();

  // First, refresh Firebase token
  const firebaseAlive = await refreshFirebaseToken();

  if (!firebaseAlive) {
    // Firebase auth has fully expired and can't be refreshed.
    // Check if the Rails session is still valid — if so, reload the page
    // so window.user gets re-populated from the server.
    // If Rails session is also gone, the reload will redirect to login
    // via authenticate_user!.
    console.warn('[SessionKeepAlive] Firebase auth expired, reloading page to restore session...');
    window.location.reload();
    return;
  }

  // Then ping the server to keep Rails session alive
  const serverAlive = await pingServer();

  if (!serverAlive && hasFirebaseUser()) {
    // Rails session expired but Firebase auth is still valid
    // Re-sync Firebase auth with Rails to restore the session
    console.log('[SessionKeepAlive] Rails session expired, re-syncing with Firebase auth...');
    const synced = await forceSync();
    if (synced) {
      console.log('[SessionKeepAlive] Successfully restored Rails session');
    } else {
      console.warn('[SessionKeepAlive] Failed to restore Rails session');
    }
  }
}

/**
 * Start the keep-alive service
 */
export function startSessionKeepAlive(): void {
  if (isRunning) {
    console.log('[SessionKeepAlive] Already running');
    return;
  }

  console.log('[SessionKeepAlive] Starting service (interval: 10 minutes)');
  isRunning = true;

  // Perform initial check
  performKeepAlive();

  // Set up periodic checks
  intervalId = setInterval(performKeepAlive, KEEP_ALIVE_INTERVAL);

  // Also refresh on visibility change (when user returns to tab)
  document.addEventListener('visibilitychange', handleVisibilityChange);
}

/**
 * Stop the keep-alive service
 */
export function stopSessionKeepAlive(): void {
  if (!isRunning) {
    return;
  }

  console.log('[SessionKeepAlive] Stopping service');
  isRunning = false;

  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }

  document.removeEventListener('visibilitychange', handleVisibilityChange);
}

/**
 * Handle visibility change - refresh session when user returns to tab
 */
function handleVisibilityChange(): void {
  if (document.visibilityState === 'visible') {
    console.log('[SessionKeepAlive] Tab became visible, refreshing session');
    performKeepAlive();
  }
}

/**
 * Check if the service is running
 */
export function isSessionKeepAliveRunning(): boolean {
  return isRunning;
}
